import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listCustomers, adminAuditLog, gdprExport, gdprAnonymize,
} from '../services/api';

export default function GdprPanel() {
  const { t } = useTranslation();
  // One list of clients; the export covers both roles (facturation, intervention).
  const kind = 'customer';
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [audit, setAudit] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const loadEntities = () => {
    listCustomers().then(setList).catch(() => {});
  };
  const loadAudit = () => adminAuditLog({ limit: 100 }).then(setAudit).catch(() => {});
  useEffect(() => { loadEntities(); loadAudit(); }, []);

  const handleExport = async () => {
    if (!selectedId) return;
    setBusy(true); setMsg('');
    try {
      const bundle = await gdprExport(kind, selectedId);
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rgpd_${kind}_${selectedId}.json`;
      a.click();
      URL.revokeObjectURL(url);
      loadAudit();
    } catch {
      setMsg(t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const handleAnonymize = async () => {
    if (!selectedId || !window.confirm(t('gdpr.anonymizeConfirm'))) return;
    setBusy(true); setMsg('');
    try {
      const res = await gdprAnonymize(kind, selectedId);
      setMsg(t('gdpr.anonymized', { count: res.invoices_retained }));
      setSelectedId('');
      loadEntities();
      loadAudit();
    } catch {
      setMsg(t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3 style={{ marginBottom: 8, fontSize: 16, fontWeight: 700 }}>{t('gdpr.title')}</h3>
      <p style={{ fontSize: 13, color: 'var(--color-text-light)', marginBottom: 16 }}>{t('gdpr.intro')}</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, alignItems: 'end', maxWidth: 520 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>{t('gdpr.choose')}</label>
          <select className="form-control" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
            <option value="">—</option>
            {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={handleExport} disabled={!selectedId || busy}>
          {t('gdpr.export')}
        </button>
        <button className="btn btn-danger" onClick={handleAnonymize} disabled={!selectedId || busy}>
          {t('gdpr.anonymize')}
        </button>
        {msg && <span style={{ fontSize: 13, color: 'var(--color-text)' }}>{msg}</span>}
      </div>
      <p style={{ fontSize: 12, color: 'var(--color-text-light)', marginTop: 8 }}>{t('gdpr.retentionNote')}</p>

      <h4 style={{ margin: '24px 0 10px', fontSize: 14, fontWeight: 700 }}>{t('gdpr.auditTitle')}</h4>
      <div style={{ overflowX: 'auto' }}>
        <table className="ai-cost-table" style={{ minWidth: 560 }}>
          <thead>
            <tr>
              <th>{t('gdpr.auditDate')}</th>
              <th>{t('gdpr.auditActor')}</th>
              <th>{t('gdpr.auditAction')}</th>
              <th>{t('gdpr.auditEntity')}</th>
            </tr>
          </thead>
          <tbody>
            {audit.length === 0 ? (
              <tr><td colSpan={4} style={{ color: 'var(--color-text-light)' }}>{t('gdpr.auditEmpty')}</td></tr>
            ) : audit.map((a) => (
              <tr key={a.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{new Date(a.created_at).toLocaleString()}</td>
                <td>{a.actor_email || '—'}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{a.action}</td>
                <td style={{ fontSize: 12 }}>{[a.entity_type, a.detail].filter(Boolean).join(' · ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
